import { memo, useEffect, useMemo, useRef, useState } from "react"
import QuickWidgetDialog from "../../../quickWidgetDialog/quickWidgetDialog";
import { QuickWidgetTypes } from "../../../../reducer/user.reducer";
import { useSelector } from "react-redux";
import { selectPrimarySecurity } from "../../../../selectors/home";
import { selectOverviewSecurity } from "../../../../selectors/security";
import { selectMarketDepthSecurity } from "../../../../selectors/common";
import MarketDepth from "../../../marketDepth/marketDepth";
import { useDispatch } from "react-redux";
import { getOrderBook } from "../../../../services/apis/candles";
import { updateOrderBooks } from "../../../../reducer/security.reducer";
import { updateMarketDepthSecurity } from "../../../../reducer/common.reducer";
import { Box, CircularProgress, Divider, useTheme } from '@mui/material'
import websocketActions from "../../../../actions/websocket.actions";
import OrderBookOverview from "../../../orderBookOverview/orderBookOverview";
import SymbolDisplay from "../../../symbolDisplay/symbolDisplay";
import { getSecurityAsync, getSecurityKey } from "../../../../util/security"
import { Security, SecurityCategory_CategoryCode } from "../../../../proto/generated/security";
import { isTradeableSecurity } from "../../../../util/cashInstrument";
import { useLocation } from "react-router-dom";

const MarketDepthWidget: React.FC = () => {

    const theme = useTheme()
    const dispatch = useDispatch();
    const location = useLocation();
    const { primary: security, expressionMode, expressionSecurities, key } = useSelector(selectPrimarySecurity)
    const marketDepthSecurity = useSelector(selectMarketDepthSecurity)
    const overviewSecurityKey = useSelector(selectOverviewSecurity)
    
    // Use overview security for screener pages, market depth security for others
    const isScreenerPage = location.pathname.includes('/screener')
    
    const [displaySecurity, setDisplaySecurity] = useState<Security | undefined>(undefined)
    const [fallbackSecurity, setFallbackSecurity] = useState<Security | undefined>(undefined)
    const niftyFallbackRun = useRef(false)

    // Determine which security to use based on priority
    const securityToUse = useMemo(() => {
        if (isScreenerPage && overviewSecurityKey) {
            // On screener pages, use overview security if available
            return overviewSecurityKey
        } else if (marketDepthSecurity) {
            // Use market depth security if available
            return marketDepthSecurity
        } else if (security) {
            // Use primary security if available
            return security
        } else {
            // Use fallback security as last resort
            return fallbackSecurity
        }
    }, [isScreenerPage, overviewSecurityKey, marketDepthSecurity, security, fallbackSecurity])

    // Update market depth security when primary changes (if different and not on screener)
    useEffect(() => {
        if (security && !isScreenerPage) {
            // Check if primary security is different from current market depth security
            const primaryKey = getSecurityKey(security.exchange, security.code)
            const currentMarketDepthKey = marketDepthSecurity ? getSecurityKey(marketDepthSecurity.exchange, marketDepthSecurity.code) : null
            
            if (primaryKey !== currentMarketDepthKey) {
                dispatch(updateMarketDepthSecurity(security))
            }
        }
    }, [security, marketDepthSecurity, dispatch, isScreenerPage])

    // NIFTY_50 fallback: only when no primary, no market depth security, and not on screener
    useEffect(() => {
        if (!security && !marketDepthSecurity && !isScreenerPage && !fallbackSecurity && !niftyFallbackRun.current) {
            niftyFallbackRun.current = true
            const timer = setTimeout(() => {
                getSecurityAsync('NSE:NIFTY_50').then(sec => {
                    if (sec) {
                        setFallbackSecurity(sec)
                    }
                })
            }, 50)
            return () => clearTimeout(timer)
        }
    }, [security, marketDepthSecurity, isScreenerPage, fallbackSecurity])

    // Reset fallback when we have a proper security
    useEffect(() => {
        if ((security || marketDepthSecurity) && fallbackSecurity) {
            setFallbackSecurity(undefined)
            niftyFallbackRun.current = false
        }
    }, [security, marketDepthSecurity, fallbackSecurity])

    // Load the actual Security object from the determined security
    useEffect(() => {
        if (securityToUse) {
            if (typeof securityToUse === 'string') {
                // It's a security key, need to load the Security object
                getSecurityAsync(securityToUse).then(sec => {
                    setDisplaySecurity(sec)
                })
            } else {
                // It's already a Security object
                setDisplaySecurity(securityToUse)
            }
        } else {
            setDisplaySecurity(undefined)
        }
    }, [securityToUse])


    useEffect(() => {
        if (displaySecurity) {
            getOrderBook({
                security: displaySecurity,
                dispatch: dispatch
            })
                .then((result) => {
                    const orderBook = { ...result }
                    if (!orderBook.security) {
                        orderBook.security = { ...displaySecurity }
                    }
                    dispatch(updateOrderBooks([orderBook]))
                })
                .catch((error) => {
                    console.error('Error fetching order book:', error)
                })

            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: [{
                            exchange: displaySecurity.exchange,
                            code: displaySecurity.code
                        }],
                        topBidAskOnly: false
                    }
                ]
            })
        }
        return () => {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: [],
                        topBidAskOnly: false
                    }
                ]
            })
        }
    }, [displaySecurity])

    const title = useMemo(() => {
        return <SymbolDisplay
            securityList={expressionMode ? expressionSecurities : (displaySecurity ? [displaySecurity] : [])}
            expression={key || ''}
            expressionMode={expressionMode}
            expressionName={''}
            fontSize={12}
            logoSize={20}
            maxWidth="auto"
            displayEvents={false}
        />
    }, [expressionMode, key, expressionSecurities, displaySecurity])


    return <QuickWidgetDialog widgetType={QuickWidgetTypes.MARKET_DEPTH} title={title} >

        {expressionMode ?
            <Box padding={4} textAlign="center">Depth not available for expressions.</Box> : (
                displaySecurity ? (
                    <>
                    {
                        displaySecurity.categoryCode === SecurityCategory_CategoryCode.INDEX ? null
                        : <>
                            <Box padding={theme.spacing(0, 1, 0.5)}>
                                <MarketDepth security={displaySecurity} tradeable={isTradeableSecurity(displaySecurity)} />
                            </Box>
                            <Divider />
                        </>
                    }
                        <Box padding={theme.spacing(1)}>
                            <OrderBookOverview security={displaySecurity} />
                        </Box>
                    </>) : <Box padding={4} textAlign="center"><CircularProgress size={20} /></Box>
            )
        }

    </QuickWidgetDialog>
}

export default memo(MarketDepthWidget);